// Seed for Advisory › Driver tree — Paul's `module03_driver_tree_v2.html` six
// issues (structure, impact spine, tiers, severities, per-role actions), with
// run-time rulings applied everywhere (docs/plans/glass-poc-run/decisions.md):
//   - issue #2's path uses the role-dashboards spelling `Throughput and Zn rec`.
//   - Unreconciled derived ppt / component-dollar figures are not in renderable
//     fields; the transform grounds prose numbers from each card's series
//     (issues 1, 2, 3, 6) or drops the additive attribution (issues 3–4).
// Rule-id codes from the MD-0nn family are held on `hypotheses[].code` as an
// empty string and never printed — docs/foundations/product-vision.md §
// Absence is not content keeps that vocabulary inside Advisory › Rule set and
// the case drawer only; BM-/CY-/RE- codes are not in that closed list and
// render as Paul drew them.
// `operatorImpactNote` / `operatorWhat` carry the amount-free rewrite of
// `impactNote` / `what` for the Operator projection (fixer round 1
// MUST-FIX 1) — Operator sees no US$ figure anywhere on this route.
// `shortLabel` + `contributesTo` replace "Contributing to Issue #3" style
// copy: issue 4 names issue 3 by its `shortLabel`, resolved at transform
// time, never by number (MUST-FIX 2).

import type { DriverEdge, DriverNode, DriverTreeSeed } from '@/versions/v1/schema/driver-tree';
import { FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { bm01BearingStopComparison } from '@/versions/v1/modules/downtime/ValueLost.transform';
import { formatCurrencyExact } from '@/versions/v1/shared/money';

const BM01_FAILURE_MODE = FAILURE_MODES.find((row) => row.id === 'fm-trunnion')!;
const BM01_STOP_COMPARISON = bm01BearingStopComparison();

export const DRIVER_TREE_NODES: DriverNode[] = [
  { id: 'ebitda', label: 'EBITDA', kind: 'financial' },
  { id: 'mill-availability', label: 'Mill availability', kind: 'driver' },
  { id: 'bm01-health', label: 'BM01 health', kind: 'driver' },
  { id: 'te-622', label: 'TE-622-625 bearing temp', kind: 'signal', unit: '°C' },
  { id: 'throughput-zn-rec', label: 'Throughput and Zn rec', kind: 'driver' },
  { id: 'cyclone-p80', label: 'Cyclone P80', kind: 'metric', unit: 'µm' },
  { id: 'mill-feed-rate', label: 'Mill feed rate', kind: 'signal', unit: 'tph' },
  { id: 'zn-nsr', label: 'Zn NSR', kind: 'driver' },
  { id: 'zn-recovery', label: 'Zn recovery', kind: 'metric', unit: '%' },
  { id: 'ph-cuso4-p80-compound', label: 'pH + CuSO4 + P80 (compound)', kind: 'signal' },
  { id: 'cuso4-activation', label: 'CuSO4 activation', kind: 'driver' },
  { id: 'fit-cuso4', label: 'FIT-CuSO4', kind: 'signal', unit: 'g/t' },
  { id: 'ph-control', label: 'pH control', kind: 'driver' },
  { id: 'lime-silo-lt500', label: 'Lime silo LT-500', kind: 'signal', unit: '%' },
  { id: 'zn-nsr-deductions', label: 'Zn NSR deductions', kind: 'driver' },
  { id: 'pb-penalty', label: 'Pb penalty', kind: 'driver' },
  { id: 'znso4-smbs-ratio', label: 'ZnSO4/SMBS ratio', kind: 'signal' },
];

export const DRIVER_TREE_EDGES: DriverEdge[] = [
  { from: 'mill-availability', to: 'ebitda' },
  { from: 'bm01-health', to: 'mill-availability' },
  { from: 'te-622', to: 'bm01-health' },
  { from: 'throughput-zn-rec', to: 'ebitda' },
  { from: 'cyclone-p80', to: 'throughput-zn-rec' },
  { from: 'mill-feed-rate', to: 'cyclone-p80' },
  { from: 'zn-nsr', to: 'ebitda' },
  { from: 'zn-recovery', to: 'zn-nsr' },
  { from: 'ph-cuso4-p80-compound', to: 'zn-recovery' },
  { from: 'cuso4-activation', to: 'zn-recovery' },
  { from: 'fit-cuso4', to: 'cuso4-activation' },
  { from: 'ph-control', to: 'zn-recovery' },
  { from: 'lime-silo-lt500', to: 'ph-control' },
  { from: 'zn-nsr-deductions', to: 'ebitda' },
  { from: 'pb-penalty', to: 'zn-nsr-deductions' },
  { from: 'znso4-smbs-ratio', to: 'pb-penalty' },
];

export const DRIVER_TREE_SEED: DriverTreeSeed = {
  asOf: '2026-09-14T09:00',
  site: 'Bunker Hill',
  nodes: DRIVER_TREE_NODES,
  edges: DRIVER_TREE_EDGES,
  issues: [
    {
      id: 'dt-1-bm01-bearing',
      rank: 1,
      title: 'BM01 ball mill bearing — temperature trending',
      shortLabel: 'BM01 bearing temperature',
      stopComparison: BM01_STOP_COMPARISON,
      attribution: 'independent',
      countsTowardTotal: false,
      exposurePeriod: 'conditional',
      impactType: 'Risk',
      impactNote: 'One-off bearing stop comparison · planned inspection versus avoided failure',
      operatorImpactNote: 'One-off bearing stop comparison',
      tarpTier: 1,
      severity: 'critical',
      path: ['ebitda', 'mill-availability', 'bm01-health', 'te-622'],
      trend: {
        label: 'TE-622-625 bearing temp (°C)',
        x: [],
        y: [],
        badDirection: 'up',
      },
      what:
        'BM01 trunnion bearing temperature is rising after the WO-441 trip. Elevated bearing temperature indicates increasing friction, which typically signals early bearing wear or lubrication degradation. Uncontrolled bearing failure causes a full mill seizure — the single largest unplanned event on site.',
      why:
        'Most likely cause (62% confidence): early bearing wear. Other candidates: lube oil viscosity degradation (28%) and mill liner imbalance (10%). Oil sample sent to lab — Fe result due 16:00 will confirm or rule out wear vs lubrication.',
      operatorActions: [
        'Log TE-622-625 temperature every hour in shift sheet.',
        'If temperature rises >1°C in any single hour, call Maintenance Supervisor immediately. Do NOT wait for next check.',
        'Confirm lube oil sight glass shows flow on next equipment walk (by 10:30).',
        'Do NOT adjust any mill operating parameter without Maintenance Supervisor approval.',
      ],
      metDiagnosis:
        `Leading diagnosis: early bearing wear (RPN ${BM01_FAILURE_MODE.rpn}) at 62% confidence. Competing: lube oil viscosity degraded 28%, mill liner imbalance 10%. Discriminating: lube oil lab result expected 16:00 — if Fe >80 ppm, upgrade to Tier 2 immediately. Financial: planned inspection ${BM01_STOP_COMPARISON.plannedHours} h ${formatCurrencyExact(BM01_STOP_COMPARISON.plannedAmount)} vs avoided failure ${BM01_STOP_COMPARISON.avoidedFailureHours} h ${formatCurrencyExact(BM01_STOP_COMPARISON.avoidedFailureAmount)}. Schedule inspection now.`,
      maintenanceActions: [
        'Handheld vibration check BM01 DE and NDE bearings before 14:00. Record vs baseline (4 Sep: 3.6 mm/s).',
        'Confirm lube oil pressure ≥280 kPa and flow in both sight glasses.',
        'Oil sample sent to lab — confirm result tracking by 16:00. Request priority.',
        `If vibration >20% above baseline OR Fe >80 ppm: schedule controlled ${BM01_STOP_COMPARISON.plannedHours}h inspection at next planned window. Pre-stage SKF 23148 bearing kit from stores now.`,
      ],
      hypotheses: [
        { code: 'BM-001', label: 'Early bearing wear', confidence: 62 },
        { code: 'BM-009', label: 'Lube oil viscosity degraded', confidence: 28 },
        { code: 'BM-005', label: 'Mill liner imbalance', confidence: 10 },
      ],
      caseId: 'case-bm-brg', // [syn] W1 r3 — same trunnion bearing asset as BM-BRG.
    },
    {
      id: 'dt-2-p80-excursion',
      rank: 2,
      title: 'P80 excursion — ore hardness change detected',
      shortLabel: 'P80 excursion',
      countedBasis: { kind: 'throughput', lostStph: 3 },
      attribution: 'owner',
      countsTowardTotal: true,
      exposurePeriod: 'active',
      impactType: 'Active loss',
      sharedPathContribution: {
        attribution: 'contributor',
        countsTowardTotal: false,
        ownerIssueId: 'dt-3-zn-recovery',
        basis: { kind: 'recovery-share', metal: 'Zn', fraction: 0.3875 }, // [syn] 0.93 / 2.4 of the parent's reading-grounded Zn recovery gap.
      },
      impactNote: 'Throughput arm · Active loss',
      tarpTier: 2,
      severity: 'critical',
      path: ['ebitda', 'throughput-zn-rec', 'cyclone-p80', 'mill-feed-rate'],
      trend: {
        label: 'PSA-001 Cyclone P80 (µm)',
        x: ['-5h', '-4h', '-3h', '-2h', '-1h', 'Now'],
        y: [86, 87, 88, 89, 91, 91],
        target: 88,
        badDirection: 'up',
      },
      what:
        'The cyclone overflow particle size (P80) has risen from 86 µm to 91 µm over the past 6 hours, meaning ore is not being ground as fine as required. A coarser grind directly reduces both zinc and lead recovery — larger particles contain locked mineral grains that flotation cannot liberate. Throughput has been reduced to compensate, compounding the production loss.',
      why:
        'Ore hardness has increased in the current stope. Harder ore requires more grinding energy to reach the same target size — at the same feed rate, the mill cannot keep up, so P80 rises. Mill feed is 71.7 stph against its 75.0 stph design, held there to bring P80 back toward the 88 µm target. Mine planning can request softer ore from an adjacent stope if hardness persists beyond today.',
      operatorActions: [
        'Feed rate held at 71.7 stph. Confirm DCS WIT-021 reads 71.7 ±1 stph. Hold if above 72.7 stph.',
        'Do NOT increase feed rate without Met sign-off, even if DCS alarm clears.',
        'Log PSA-001 P80 every 30 minutes. Target: ≤88 µm for 4 consecutive hours before any increase.',
        'Current: 91 µm. Last 3 readings: 91, 91, 90 µm — stable but still above USL.',
      ],
      metDiagnosis:
        'Ore hardness increase (78% confidence) — leading cause. Competing: cyclone spigot wear 14% (CY-04 at 11% — not primary but do not exclude), ball charge 8%. Discriminating: measure CY-04 spigot — if >13mm ID reclassify to spigot wear. Fix P80 first — affects both Pb and Zn simultaneously.',
      maintenanceActions: [
        'No active maintenance action required for this event.',
        'Monitor cyclone cluster CY-01 to CY-08. Report any increased wear at next weekly measurement (due 18 Sep).',
        'Confirm CY-04 replacement spigot is in stores in case measurement shows acceleration.',
      ],
      hypotheses: [
        { code: '', label: 'Ore hardness increase', confidence: 78 },
        { code: 'CY-001', label: 'Cyclone spigot wear', confidence: 14 },
        { code: 'BM-005', label: 'Ball charge', confidence: 8 },
      ],
      caseId: 'case-p80',
    },
    {
      id: 'dt-3-zn-recovery',
      rank: 3,
      title: 'Zn recovery shortfall (Tier 3)',
      shortLabel: 'Zn recovery shortfall',
      countedBasis: { kind: 'recovery-gap', metal: 'Zn' }, // [syn] Design minus the measured Zn recovery reading at the cursor.
      attribution: 'owner',
      countsTowardTotal: true,
      exposurePeriod: 'active',
      impactType: 'Active loss',
      impactNote: 'Owner · Active loss — compound cause',
      operatorImpactNote: 'Active loss — compound cause',
      tarpTier: 3,
      severity: 'critical',
      path: ['ebitda', 'zn-nsr', 'zn-recovery', 'ph-cuso4-p80-compound'],
      trend: {
        label: 'Zn recovery (%)',
        x: [],
        y: [],
        badDirection: 'down',
      },
      what:
        'Zinc recovery measures how much of the zinc in the ore ends up in the zinc concentrate (paid product) rather than the tailings (waste).',
      operatorWhat:
        'Zinc recovery measures how much of the zinc in the ore ends up in the zinc concentrate (paid product) rather than the tailings (waste).',
      why:
        'Three root causes are acting simultaneously: (1) pH is too low at 10.4 vs 11.0 target — caused by lime silo depletion reducing the alkalinity needed for zinc activation. (2) CuSO4 batch strength is only 162 g/L vs 180 g/L minimum — weak copper sulphate fails to adequately activate zinc sulphide minerals before flotation. (3) P80 is coarse at 91 µm vs 88 µm target — larger particles contain unliberated zinc. All three must be resolved before recovery will improve.',
      operatorActions: [
        'Do NOT adjust any Zn reagent without Met approval. This is a Tier 3 case — Met team owns the response.',
        'Confirm AT-150 pH every 15 minutes and log. Target: ≥10.8. Currently 10.4 — calibration adjustment in progress.',
        'When CuSO4 new batch is ready (~10:00), log FIT-CuSO4 reading. Target: >480 g/t within 5 min of switch.',
        'If Zn tails PSA reading rises above 0.45%, call Met immediately.',
      ],
      metDiagnosis:
        'COMPOUND CAUSE — three factors acting simultaneously: lime silo depletion, pH excursion (85% confidence) — pH partially recovered to 10.4, target 11.0; lime delivery tonight resolves root cause. CuSO4 batch quality (78% confidence) — new batch prepared, titrated 09:45; dosing held until ≥180 g/L was confirmed. P80 coarse (78% confidence). Do NOT close until two consecutive shift assays show sustained recovery improvement.',
      maintenanceActions: [
        'No maintenance action required for Zn recovery case.',
        'Ensure lime delivery truck access route is clear before 18:00.',
        'Confirm CuSO4 mixing area is clear for reagent batch preparation (~09:30).',
      ],
      hypotheses: [
        { code: '', label: 'Lime silo depletion (pH)', confidence: 85 },
        { code: '', label: 'CuSO4 batch quality', confidence: 78 },
        { code: '', label: 'P80 coarse', confidence: 78 },
      ],
      caseId: 'case-zn-recovery',
    },
    {
      id: 'dt-4-cuso4-dosing',
      rank: 4,
      title: 'CuSO4 dosing declining — batch strength 162 g/L',
      shortLabel: 'CuSO4 dosing decline',
      attribution: 'contributor',
      countsTowardTotal: false,
      exposurePeriod: 'active',
      impactType: 'Active loss',
      sharedPathContribution: {
        attribution: 'contributor',
        countsTowardTotal: false,
        ownerIssueId: 'dt-3-zn-recovery',
        basis: { kind: 'recovery-share', metal: 'Zn', fraction: 0.16666666666666666 }, // [syn] 0.4 / 2.4 of the parent's reading-grounded Zn recovery gap.
      },
      impactNote: '',
      contributesTo: 'dt-3-zn-recovery',
      tarpTier: 1,
      severity: 'high',
      path: ['ebitda', 'zn-nsr', 'zn-recovery', 'cuso4-activation', 'fit-cuso4'],
      trend: {
        label: 'FIT-CuSO4 dose (g/t)',
        x: ['-7h', '-6h', '-5h', '-4h', '-3h', '-2h', '-1h', 'Now'],
        y: [495, 490, 485, 478, 472, 468, 465, 462],
        target: 480,
        badDirection: 'down',
      },
      what:
        'The copper sulphate (CuSO4) dose rate has been declining as the current batch becomes depleted and weaker. CuSO4 is a critical activator for zinc sulphide minerals — it chemically prepares zinc surfaces to attach to flotation bubbles. When the dose or strength falls below target, zinc minerals pass through the circuit unactivated and report to the tailings, directly reducing zinc recovery.',
      why:
        'The current CuSO4 batch was prepared at substandard concentration — 162 g/L versus the 180 g/L minimum. As the weakened batch depletes, the dose rate delivered to the circuit also falls. A new batch was prepared by the Met team (target 185–200 g/L). It was titrated at 09:45 and switched once confirmed above 180 g/L.',
      operatorActions: [
        'New batch prepared by the Met team and switched to the new tank at 10:00; FIT-CuSO4 logged within 5 min of the switch, target >480 g/t.',
        'Do NOT switch again until Met confirms titration ≥180 g/L.',
        'Current FIT-CuSO4: 462 g/t. If drops below 460 g/t before batch is ready, call Met immediately — Tier 2 triggers.',
      ],
      metDiagnosis:
        'CuSO4 batch quality (78% confidence) — leading cause. Competing: pH suppressing activation (14%), pump fault (8%). New batch mixed — titrated 09:45, target 185–200 g/L. A sub-180 g/L titration takes 15 kg crystals and a 15 min re-titrate. Note: even with correct CuSO4, activation will be sub-optimal until pH recovers above 11.0.',
      maintenanceActions: [
        'Inspected the CuSO4 dosing pump seal and suction strainer during the 10:00 batch switch. Takes 5 minutes. Confirms pump is not a contributing cause.',
        'If mechanical issue found: raise CMMS work order CuSO4-PUMP-001.',
      ],
      hypotheses: [
        { code: '', label: 'CuSO4 batch quality', confidence: 78 },
        { code: '', label: 'pH suppressing activation', confidence: 14 },
        { code: 'RE-001', label: 'Pump fault', confidence: 8 },
      ],
      caseId: 'case-cuso4-closed', // [syn] W1 r3 — the resolved case names the CuSO4 dosing cause.
      resolvedAt: '2026-06-08T09:55', // [syn] W1 r4 — same resolution clock as case-cuso4-closed.
    },
    {
      id: 'dt-5-lime-silo',
      rank: 5,
      title: 'Lime silo LT-500 at 26% — below 30% LSL',
      shortLabel: 'Lime silo LT-500 low',
      attribution: 'contributor',
      countsTowardTotal: false,
      exposurePeriod: 'active',
      impactType: 'Risk',
      sharedPathContribution: {
        attribution: 'contributor',
        countsTowardTotal: false,
        ownerIssueId: 'dt-3-zn-recovery',
        basis: { kind: 'recovery-share', metal: 'Zn', fraction: 0.44583333333333336 }, // [syn] 1.07 / 2.4 of the parent's reading-grounded Zn recovery gap.
      },
      impactNote: 'Risk — delivery confirmed 21:00 tonight',
      contributesTo: 'dt-3-zn-recovery',
      tarpTier: 2,
      severity: 'high',
      path: ['ebitda', 'zn-nsr', 'zn-recovery', 'ph-control', 'lime-silo-lt500'],
      trend: {
        label: 'LT-500 Lime silo level (%)',
        x: ['-7h', '-6h', '-5h', '-4h', '-3h', '-2h', '-1h', 'Now'],
        y: [38, 35, 33, 31, 29, 28, 27, 26],
        target: 30,
        alarm: 20,
        badDirection: 'down',
      },
      what:
        'The lime silo level has dropped from 38% to 26% over the past 8 hours and is now below the 30% low-level alarm. Lime (calcium hydroxide) controls the alkalinity (pH) in the zinc flotation circuit. Without sufficient lime, pH falls below the 11.0 operating target, reducing zinc mineral activation and directly suppressing zinc recovery. The silo must not fall below 20% before the 21:00 delivery arrives.',
      why:
        'Lime consumption has depleted the silo faster than the replenishment schedule anticipated. A delivery has been confirmed for 21:00 tonight. If the silo drops below 20% before delivery, manual lime addition from bagged product stored in the reagent shed will be required (approximately 2 hours of supply available). The lime silo depletion is the primary root cause of the current pH excursion and Zn recovery shortfall.',
      operatorActions: [
        'Check LT-500 level every 30 min this shift. Log in shift sheet.',
        'If LT-500 drops below 20% before delivery: switch both Pb and Zn circuits to manual lime addition. Rates: Pb 4.2 kg/t, Zn 2.8 kg/t. Bagged lime in reagent shed — 14 bags (~2 hours supply).',
        'Notify Night shift supervisor: delivery expected ~21:00. Return to AUTO only after LT-500 reads ≥45%.',
      ],
      metDiagnosis:
        'Lime silo depletion (85% confidence) — primary driver of the current Zn recovery excursion. Lime delivery tonight resolves root cause. Manual addition procedure (SOP-REAG-LIME-001) is containment while awaiting delivery. Recovery expected to lag 2–4 hours behind pH restoration. Ensure Night shift Met is briefed on handover.',
      maintenanceActions: [
        'Confirm lime delivery access route is clear — no equipment blocking silo hatch road before 18:00.',
        'Confirm P100 respirators available at silo hatch for delivery (SOP-CHEM-001).',
        'Confirm dust suppression system operational before delivery arrives.',
      ],
      hypotheses: [{ code: '', label: 'Lime silo depletion', confidence: 85 }],
      caseId: 'case-zn-rgh-ph', // [syn] W1 r4 — silo remains below LSL while delivery is due at 21:00.
    },
    {
      id: 'dt-6-pb-in-zn-con',
      rank: 6,
      title: 'Pb in Zn concentrate — trending toward smelter penalty',
      shortLabel: 'Pb in Zn con',
      countedBasis: { kind: 'schedule-penalty', product: 'Zn', element: 'Pb' },
      attribution: 'independent',
      countsTowardTotal: true,
      exposurePeriod: 'active',
      impactType: 'Accruing penalty',
      impactNote: 'Watch · below smelter penalty threshold',
      operatorImpactNote: 'Watch · below smelter penalty threshold',
      tarpTier: 'watch',
      severity: 'medium',
      path: ['ebitda', 'zn-nsr-deductions', 'pb-penalty', 'znso4-smbs-ratio'],
      trend: {
        label: 'SA03 Pb in Zn con (%)',
        x: ['8 Sep', '9 Sep', '10 Sep', '11 Sep', '12 Sep', '13 Sep', 'Now'],
        y: [2.0, 2.1, 2.2, 2.2, 2.3, 2.4, 2.4],
        target: 2.0,
        alarm: 3.0,
        badDirection: 'up',
      },
      what:
        'The lead content in the zinc concentrate has been rising steadily from 2.0% to 2.4% over 7 days. If it reaches 3.0%, the smelter will apply an impurity penalty deduction to the zinc concentrate payment. Lead in the zinc concentrate means the flotation circuit is not selectively rejecting lead when recovering zinc — both minerals are reporting to the zinc product.',
      why:
        'The ZnSO4/SMBS reagent ratio has declined from 0.86 to 0.82 over 8 consecutive readings. SMBS (sodium metabisulphite) is used to depress (suppress) lead in the zinc circuit — an insufficient dose allows lead minerals to float with the zinc and report to the zinc concentrate. The Metallurgist has proposed a 12% SMBS dose increase. At 2.4% there is 0.6 pt of headroom before the 3.0% penalty triggers.',
      operatorActions: [
        'No immediate action — monitor only at this stage while the five other open process issues are active.',
        'Log SA03 Pb% from each 12-hourly composite. Next result due ~21:00.',
        'When Met approves SMBS adjustment, implement and log new dose rate and time.',
      ],
      metDiagnosis:
        'Pb deportment (72% confidence). ZnSO4/SMBS dose ratio declined 0.86→0.82 over 8 readings — primary driver of reduced selectivity. Increase SMBS dose by 12%. At 2.4% there is 0.6 pt headroom before the 3.0% penalty trigger.',
      maintenanceActions: ['No maintenance action required. Reagent selectivity issue.'],
      hypotheses: [{ code: '', label: 'Pb deportment — ZnSO4/SMBS ratio decline', confidence: 72 }],
    },
  ],
};
